/**
 * Meeting Autopilot, app side: pure helpers (no React, no network) that turn what the worker
 * stored into the shapes the app renders, and decide what Undo and the `/go/generate` link may do.
 *
 * Where the worker's per-session record lives: the session's one ai_requests row of type
 * 'meeting_autopilot' (migration 20261108000100 allows one per session), in
 * result_data.autopilot (cloud-run-worker/meetingAutopilot.js documents the shape). The reader
 * also accepts result_data itself and snake_case keys.
 * Insights columns: session_insights.summary / actions (existing), decisions (jsonb),
 * follow_up (jsonb), meeting_type (text). The 3 short lines are the record's summaryLines.
 */

import { isTemplateUsableForGeneration } from '../utils/templateListSelect';
import { canonicalJson, fnv1a32Hex } from '../constants/aiModels';

/** Strict UUID (ids in links and records). */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Per-user Autopilot settings and their defaults (all on). */
export const AUTOPILOT_SETTING_KEYS = Object.freeze(['autoSummaries', 'autoFiling', 'autoCreate', 'meetingRecaps']);
export const DEFAULT_AUTOPILOT_SETTINGS = Object.freeze({
  autoSummaries: true,
  autoFiling: true,
  autoCreate: true,
  meetingRecaps: true,
});
/** Stored (snake_case) key for each setting. */
const STORED_SETTING_KEYS = Object.freeze({
  autoSummaries: 'auto_summaries',
  autoFiling: 'auto_filing',
  autoCreate: 'auto_create',
  meetingRecaps: 'meeting_recaps',
});

/** Meeting types the worker classifies into. */
export const MEETING_TYPES = Object.freeze(['kickoff', 'discovery', 'status', 'workshop', 'sales', 'internal', 'other']);

/** Statuses a job or step can report. */
const RUN_STATUSES = new Set(['queued', 'pending', 'running', 'processing', 'completed', 'failed', 'skipped', 'cancelled', 'ok', 'partial']);

/**
 * Whether the Autopilot flag is on for an account. The flag lives in `accounts.features.autopilot`
 * (or `accounts.settings.features.autopilot` when there is no features column).
 * @param {object|null|undefined} account - Active account row.
 * @returns {boolean} True when on.
 */
export function isAutopilotEnabled(account) {
  if (!account || typeof account !== 'object') return false;
  return account.features?.autopilot === true || account.settings?.features?.autopilot === true;
}

/**
 * Read a key that may be camelCase or snake_case.
 * @param {object} obj - Source.
 * @param {...string} keys - Candidate keys, first match wins.
 * @returns {*} Value or undefined.
 */
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
}

/**
 * Trimmed string, or '' for anything that is not a string or number.
 * @param {*} value - Value.
 * @param {number} [max] - Max length (an ellipsis marks the cut).
 * @returns {string} Text.
 */
export function text(value, max = 0) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const flat = String(value).replace(/\s+/g, ' ').trim();
  if (max > 0 && flat.length > max) return `${flat.slice(0, max - 1).trimEnd()}…`;
  return flat;
}

/**
 * Normalize a job or step status.
 * @param {*} value - Raw status.
 * @returns {'queued'|'running'|'completed'|'failed'|'skipped'|'cancelled'|'unknown'} Status.
 */
export function normalizeStatus(value) {
  const raw = String(value || '').toLowerCase();
  if (!RUN_STATUSES.has(raw)) return 'unknown';
  if (raw === 'pending') return 'queued';
  if (raw === 'processing') return 'running';
  // The worker writes 'ok' for a step and 'partial' for a run where a step failed (the rest
  // finished, and only what succeeded is shown).
  if (raw === 'ok' || raw === 'partial') return 'completed';
  return raw;
}

/**
 * Normalize the per-user Autopilot settings (missing keys default to on).
 * @param {object|null|undefined} raw - Stored settings (camelCase or snake_case).
 * @returns {{autoSummaries: boolean, autoFiling: boolean, autoCreate: boolean, meetingRecaps: boolean}} Settings.
 */
export function normalizeAutopilotSettings(raw) {
  const out = { ...DEFAULT_AUTOPILOT_SETTINGS };
  if (!raw || typeof raw !== 'object') return out;
  AUTOPILOT_SETTING_KEYS.forEach((key) => {
    const value = pick(raw, key, STORED_SETTING_KEYS[key]);
    if (typeof value === 'boolean') out[key] = value;
  });
  return out;
}

/**
 * Settings in their stored (snake_case) form.
 * @param {object} settings - Settings (any form).
 * @returns {{auto_summaries: boolean, auto_filing: boolean, auto_create: boolean, meeting_recaps: boolean}} Stored form.
 */
export function toStoredAutopilotSettings(settings) {
  const normalized = normalizeAutopilotSettings(settings);
  const out = {};
  AUTOPILOT_SETTING_KEYS.forEach((key) => {
    out[STORED_SETTING_KEYS[key]] = normalized[key];
  });
  return out;
}

/**
 * Normalize follow-up detection. Returns null unless the worker said one is needed and named a topic.
 * @param {object|null|undefined} raw - session_insights.follow_up (or the record's followUp).
 * @returns {{topic: string, reason: string, suggestedTiming: string, suggestedAttendees: string[], evidenceQuote: string}|null} Follow-up.
 */
export function normalizeFollowUp(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (pick(raw, 'needed') !== true) return null;
  const topic = text(pick(raw, 'topic'), 160);
  if (!topic) return null;
  const attendeesRaw = pick(raw, 'suggestedAttendees', 'suggested_attendees', 'attendees');
  const attendees = (Array.isArray(attendeesRaw) ? attendeesRaw : [])
    .map((a) => (a && typeof a === 'object' ? text(a.name || a.email) : text(a)))
    .filter(Boolean)
    .slice(0, 12);
  return {
    topic,
    reason: text(pick(raw, 'reason'), 400),
    suggestedTiming: text(pick(raw, 'suggestedTiming', 'suggested_timing', 'timing'), 80),
    suggestedAttendees: [...new Set(attendees)],
    evidenceQuote: text(pick(raw, 'evidenceQuote', 'evidence_quote', 'evidence'), 200),
  };
}

/**
 * Normalize decisions (jsonb array of strings or {text, speaker}).
 * @param {*} raw - session_insights.decisions.
 * @returns {string[]|null} Decision lines, or null when the column holds nothing usable.
 */
export function normalizeDecisions(raw) {
  if (!Array.isArray(raw)) return null;
  return raw
    .map((d) => {
      if (d && typeof d === 'object') {
        const body = text(d.text || d.decision || d.title);
        const speaker = text(d.speaker || d.owner);
        return body ? `${body}${speaker ? ` (${speaker})` : ''}` : '';
      }
      return text(d);
    })
    .filter(Boolean);
}

/**
 * Normalize one action item (a stored "Owner: task (due x)" string or an object).
 * @param {*} raw - Action.
 * @returns {{owner: string|null, task: string, due: string|null}} Parts.
 */
export function normalizeActionItem(raw) {
  if (raw && typeof raw === 'object') {
    return {
      owner: text(raw.owner) || null,
      task: text(raw.task || raw.text || raw.title || raw.description),
      due: text(raw.due || raw.dueDate || raw.due_date) || null,
    };
  }
  let body = text(raw);
  let due = null;
  const dueMatch = /\s*\(due ([^)]+)\)\s*$/i.exec(body);
  if (dueMatch) {
    due = dueMatch[1].trim();
    body = body.slice(0, dueMatch.index).trim();
  }
  let owner = null;
  const ownerMatch = /^([^:]{1,60}):\s+(.+)$/.exec(body);
  if (ownerMatch && !/^https?$/i.test(ownerMatch[1])) {
    owner = ownerMatch[1].trim();
    body = ownerMatch[2].trim();
  }
  return { owner, task: body, due };
}

/**
 * The summary part of the stored summary text (before "Key decisions:" / "Open questions:").
 * @param {string} summary - session_insights.summary.
 * @returns {string} Summary paragraphs.
 */
function summaryBody(summary) {
  const source = String(summary || '').replace(/\r\n/g, '\n');
  const cut = source.search(/\n*(Key decisions|Open questions):\s*\n/i);
  return (cut >= 0 ? source.slice(0, cut) : source).trim();
}

/**
 * The 3 short summary lines: the worker's `summary_lines` when present, else the first sentences
 * of the stored summary.
 * @param {object|null} insights - session_insights row.
 * @param {object|null} [record] - Normalized autopilot record (may carry summaryLines).
 * @returns {string[]} Up to 3 lines.
 */
export function summaryLinesOf(insights, record = null) {
  const explicit = pick(insights || {}, 'summary_lines', 'summaryLines') || record?.summaryLines;
  if (Array.isArray(explicit)) {
    const lines = explicit.map((l) => text(l, 200)).filter(Boolean);
    if (lines.length) return lines.slice(0, 3);
  }
  const body = summaryBody(insights?.summary);
  if (!body) return [];
  const sentences = body
    .split(/\n+/)
    .flatMap((para) => para.replace(/([.!?])\s+(?=[A-Z0-9])/g, '$1\n').split('\n'))
    .map((s) => text(s, 200))
    .filter(Boolean);
  return sentences.slice(0, 3);
}

/**
 * Normalize the filing outcome.
 * @param {object|null|undefined} raw - Filing record.
 * @returns {object|null} Filing, or null when there is none.
 */
export function normalizeFiling(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const rawOutcome = String(pick(raw, 'outcome', 'action', 'result', 'decision') || '').toLowerCase();
  const createdProject = pick(raw, 'createdProject', 'created_project', 'projectCreated', 'project_created') === true
    || rawOutcome === 'created_project' || rawOutcome === 'created_customer' || rawOutcome === 'create_project' || rawOutcome === 'create_customer';
  const createdCustomer = pick(raw, 'createdCustomer', 'created_customer', 'customerCreated', 'customer_created') === true
    || rawOutcome === 'created_customer' || rawOutcome === 'create_customer';
  const rawProjectId = text(pick(raw, 'projectId', 'project_id'));
  const rawCustomerId = text(pick(raw, 'customerId', 'customer_id'));
  const projectId = UUID_RE.test(rawProjectId) ? rawProjectId : null;
  const customerId = UUID_RE.test(rawCustomerId) ? rawCustomerId : null;
  let outcome = 'unfiled';
  // The session already had a project (the user filed it, or filed it while autopilot ran):
  // autopilot did not file it, so there is nothing to label or undo.
  if (/^already/.test(rawOutcome)) outcome = 'already_filed';
  else if (createdCustomer && projectId) outcome = 'created_customer';
  else if (createdProject && projectId) outcome = 'created_project';
  else if (projectId) outcome = 'linked';
  else if (/internal/.test(rawOutcome)) outcome = 'internal';
  else if (/limit/.test(rawOutcome)) outcome = 'limit';
  else if (/skip|already/.test(rawOutcome)) outcome = 'skipped';
  const confidence = Number(pick(raw, 'confidence'));
  return {
    outcome,
    projectId,
    customerId,
    projectName: text(pick(raw, 'projectName', 'project_name'), 120),
    customerName: text(pick(raw, 'customerName', 'customer_name'), 120),
    createdProject: Boolean(createdProject && projectId && outcome !== 'already_filed'),
    createdCustomer: Boolean(createdCustomer && projectId && outcome !== 'already_filed'),
    confidence: Number.isFinite(confidence) ? confidence : null,
    reason: text(pick(raw, 'reason', 'message'), 300),
  };
}

/**
 * Normalize template suggestions (at most 2, ids only from the record).
 * @param {*} raw - Suggestions array.
 * @returns {Array<{templateId: string, name: string, reason: string}>} Suggestions.
 */
export function normalizeTemplateSuggestions(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((s) => {
    if (!s || typeof s !== 'object') return;
    const templateId = text(pick(s, 'templateId', 'template_id', 'id'));
    if (!UUID_RE.test(templateId) || seen.has(templateId)) return;
    seen.add(templateId);
    out.push({ templateId, name: text(pick(s, 'name', 'templateName', 'template_name'), 80), reason: text(pick(s, 'reason'), 200) });
  });
  return out.slice(0, 2);
}

/**
 * Normalize a step record ({status, error} or a bare status string).
 * @param {*} raw - Step.
 * @returns {{status: string, error: string}} Step.
 */
function normalizeStep(raw) {
  if (typeof raw === 'string') return { status: normalizeStatus(raw), error: '', at: '', keptExistingSummary: false };
  if (!raw || typeof raw !== 'object') return { status: 'unknown', error: '', at: '', keptExistingSummary: false };
  return {
    status: normalizeStatus(pick(raw, 'status', 'state')),
    error: text(pick(raw, 'error', 'errorMessage', 'error_message'), 300),
    at: text(pick(raw, 'at')),
    keptExistingSummary: raw.keptExistingSummary === true,
  };
}

/**
 * Normalize the worker's per-session autopilot record. Accepts the record itself or an
 * ai_requests row (its status and result_data).
 * @param {object|null|undefined} raw - Record.
 * @param {object|null} [request] - ai_requests row for the same session (status while it runs).
 * @returns {object|null} Normalized record, or null when there is nothing.
 */
export function normalizeAutopilotRecord(raw, request = null) {
  const resultData = request?.result_data && typeof request.result_data === 'object' ? request.result_data : null;
  const source = (raw && typeof raw === 'object' ? raw : null)
    || (resultData && typeof resultData.autopilot === 'object' ? resultData.autopilot : null)
    || resultData;
  if (!source && !request) return null;
  const rec = source || {};
  const steps = rec.steps && typeof rec.steps === 'object' ? rec.steps : {};
  const filingRaw = pick(rec, 'filing') || (steps.filing && typeof steps.filing === 'object' ? steps.filing : null);
  const templatesRaw = pick(rec, 'templates', 'suggestedTemplates', 'suggested_templates', 'templateSuggestions', 'template_suggestions')
    || steps.templates?.suggestions || steps.template_suggestion?.suggestions;
  const notify = rec.notify && typeof rec.notify === 'object' ? rec.notify : null;
  // Worker shape: notify.email = { status: 'sent'|'skipped'|'failed'|'pending', reason }.
  const emailRaw = (notify?.email && typeof notify.email === 'object' ? notify.email : null)
    || pick(rec, 'email') || steps.email || null;
  const requestStatus = request ? normalizeStatus(request.status) : 'unknown';
  const recordStatus = normalizeStatus(pick(rec, 'status', 'state'));
  // The job row wins while it runs and when it failed or was cancelled (a crashed run leaves its
  // last checkpoint behind, which never reads as finished).
  const status = ['queued', 'running', 'failed', 'cancelled'].includes(requestStatus)
    ? requestStatus
    : recordStatus !== 'unknown' ? recordStatus : requestStatus;
  const sessionId = text(pick(rec, 'sessionId', 'session_id') || pick(request?.request_data || {}, 'sessionId', 'session_id'));
  return {
    sessionId: UUID_RE.test(sessionId) ? sessionId : null,
    status,
    progressMessage: text(request?.progress_message, 160),
    meetingType: MEETING_TYPES.includes(String(pick(rec, 'meetingType', 'meeting_type') || '').toLowerCase())
      ? String(pick(rec, 'meetingType', 'meeting_type')).toLowerCase()
      : null,
    summaryLines: Array.isArray(pick(rec, 'summaryLines', 'summary_lines')) ? pick(rec, 'summaryLines', 'summary_lines').map((l) => text(l, 200)).filter(Boolean).slice(0, 3) : null,
    steps: {
      insights: normalizeStep(steps.insights),
      filing: normalizeStep(steps.filing),
      requirements: normalizeStep(steps.requirements),
      templates: normalizeStep(steps.templates || steps.template_suggestion),
      notify: normalizeStep(steps.notify || steps.email),
    },
    filing: normalizeFiling(filingRaw),
    templates: normalizeTemplateSuggestions(templatesRaw),
    followUp: normalizeFollowUp(pick(rec, 'followUp', 'follow_up')),
    email: emailRaw && typeof emailRaw === 'object'
      ? {
        sent: pick(emailRaw, 'sent', 'emailSent', 'email_sent') === true || emailRaw.status === 'sent' || emailRaw.previous === 'sent',
        skippedReason: emailRaw.status === 'sent' ? '' : text(pick(emailRaw, 'skippedReason', 'skipped_reason', 'reason'), 160),
      }
      : { sent: false, skippedReason: '' },
    // "Ready for you" lists a finished run only when the worker found something worth showing
    // (notify.inApp / readyAt). Records without the key (older shapes) count as shown.
    inApp: notify && typeof notify.inApp === 'boolean' ? notify.inApp : Boolean(pick(rec, 'readyAt', 'ready_at')) || !notify,
    completedAt: text(pick(rec, 'completedAt', 'completed_at') || (requestStatus === 'completed' ? request?.updated_at : '')),
  };
}

/**
 * Whether the record says the job is still working.
 * @param {object|null} record - Normalized record.
 * @returns {boolean} True while queued or running.
 */
export function isAutopilotRunning(record) {
  return Boolean(record) && (record.status === 'queued' || record.status === 'running');
}

/**
 * Whether the session_insights row on screen is the one Autopilot wrote: its insights step
 * completed without keeping an earlier summary, and the row was not written again after that
 * step (a hand Generate / Regenerate rewrites it later).
 * @param {object|null} record - Normalized autopilot record.
 * @param {object|null} row - session_insights row.
 * @returns {boolean} True when Autopilot wrote what is shown.
 */
export function insightsWrittenByAutopilot(record, row) {
  const step = record?.steps?.insights;
  if (!row || !step || step.status !== 'completed' || step.keptExistingSummary) return false;
  const written = Date.parse(row.updated_at || row.created_at || '');
  const ran = Date.parse(step.at || record.completedAt || '');
  if (!Number.isFinite(written) || !Number.isFinite(ran)) return false;
  // A few seconds for the worker and the database clocks.
  return written <= ran + 5000;
}

/**
 * Label for the filing line ("Acme > Discovery", plus "new project" / "new customer").
 * @param {object|null} filing - Normalized filing.
 * @param {{project?: object|null, customer?: object|null}} [live] - Current rows (names win over the record's copy).
 * @returns {{path: string, badge: string|null}} Label parts.
 */
export function filingLabel(filing, live = {}) {
  const projectName = text(live.project?.name) || filing?.projectName || 'Project';
  const customerName = text(live.customer?.name) || filing?.customerName || '';
  const path = customerName ? `${customerName} > ${projectName}` : projectName;
  let badge = null;
  if (filing?.createdCustomer) badge = 'new customer';
  else if (filing?.createdProject) badge = 'new project';
  return { path, badge };
}

/**
 * Where the session sits now compared with the autopilot's filing.
 * @param {object|null} filing - Normalized filing.
 * @param {string|null} currentProjectId - sessions.project_id now.
 * @returns {'filed'|'moved'|'undone'|'unfiled'|'none'} 'filed' when it is still where autopilot put it.
 */
export function filingState(filing, currentProjectId) {
  if (!filing || filing.outcome === 'already_filed') return currentProjectId ? 'none' : 'unfiled';
  if (filing.projectId) {
    if (currentProjectId === filing.projectId) return 'filed';
    return currentProjectId ? 'moved' : 'undone';
  }
  return currentProjectId ? 'none' : 'unfiled';
}

/**
 * Why a meeting was left unfiled, in words.
 * @param {object|null} filing - Normalized filing.
 * @returns {string} Sentence.
 */
export function unfiledReason(filing) {
  switch (filing?.outcome) {
    case 'internal':
      return 'Internal meeting, so Quill left it unfiled.';
    case 'limit':
      return 'Your plan is at its project or customer limit, so Quill did not create one. File it yourself or upgrade.';
    default:
      break;
  }
  // The worker's reason is a machine code (cloud-run-worker/filing.js); never show it raw.
  const reason = String(filing?.reason || '');
  if (reason === 'internal') return 'Internal meeting, so Quill left it unfiled.';
  if (reason.startsWith('plan_limit')) return unfiledReason({ outcome: 'limit' });
  return UNFILED_REASON_TEXT[reason] || 'Quill was not sure where this meeting belongs.';
}

/** Words for the worker's unfiled reason codes (cloud-run-worker/filing.js, meetingAutopilot.js). */
const UNFILED_REASON_TEXT = Object.freeze({
  auto_filing_off: 'Automatic filing is off in your Autopilot settings.',
  auto_create_off: 'No existing project matched, and creating projects is off in your Autopilot settings.',
  trivial_meeting: 'Short call, so Quill did not create a project for it.',
  no_match: 'Quill could not match this meeting to a customer or project.',
  model_failed: 'Autopilot could not file this meeting.',
  filed_meanwhile: 'This meeting was filed while Autopilot was working.',
});

/**
 * Template suggestions the user can act on: the template still exists in the workspace and can
 * generate (not importing, processing or failed). Live names win over the record's copy.
 * @param {Array<object>} suggestions - Normalized suggestions.
 * @param {Array<object>} templates - AppContext templates.
 * @returns {Array<{templateId: string, name: string, reason: string, templateType: string}>} Usable suggestions.
 */
export function usableTemplateSuggestions(suggestions, templates) {
  const byId = new Map((templates || []).map((t) => [t.id, t]));
  return (suggestions || [])
    .map((s) => ({ s, t: byId.get(s.templateId) }))
    .filter(({ t }) => t && isTemplateUsableForGeneration(t))
    .map(({ s, t }) => ({ templateId: s.templateId, name: text(t.name) || s.name || 'template', reason: s.reason, templateType: t.templateType || 'document' }));
}

/**
 * The in-app one-click link for a template suggestion.
 * @param {string} sessionId - Session id.
 * @param {string} templateId - Template id.
 * @param {'email'|'app'} [source] - Where the click came from.
 * @returns {string} Path (ids only, nothing else in the query).
 */
export function goGeneratePath(sessionId, templateId, source = 'app') {
  const params = new URLSearchParams({ session: sessionId, template: templateId, source: source === 'email' ? 'email' : 'app' });
  return `/go/generate?${params.toString()}`;
}

/**
 * Parse the `/go/generate` query.
 * @param {URLSearchParams} params - Query.
 * @returns {{ok: true, sessionId: string, templateId: string, source: 'email'|'app'}|{ok: false, reason: 'bad_link'}} Parsed link.
 */
export function parseGoGenerateParams(params) {
  const sessionId = String(params?.get?.('session') || '').trim();
  const templateId = String(params?.get?.('template') || '').trim();
  if (!UUID_RE.test(sessionId) || !UUID_RE.test(templateId)) return { ok: false, reason: 'bad_link' };
  return { ok: true, sessionId, templateId, source: params.get('source') === 'email' ? 'email' : 'app' };
}

/**
 * Decide whether a `/go/generate` link may start a generation for this user.
 * @param {object} args - Inputs.
 * @param {object|null} args.session - sessions row as RLS returned it (id, project_id, account_id) or null.
 * @param {string|null} args.activeAccountId - Active account.
 * @param {Array<object>} args.projects - Projects the user owns or that are shared with them (AppContext).
 * @param {Array<object>} args.templates - Workspace templates (AppContext).
 * @param {string} args.templateId - Template from the link.
 * @returns {{ok: true, project: object, template: object}|{ok: false, reason: string, accountId?: string}} Decision.
 */
export function resolveGoGenerateTarget({ session, activeAccountId, projects, templates, templateId }) {
  if (!session || !session.id) return { ok: false, reason: 'not_found' };
  if (session.account_id && activeAccountId && session.account_id !== activeAccountId) {
    return { ok: false, reason: 'other_account', accountId: session.account_id };
  }
  if (!session.project_id) return { ok: false, reason: 'unfiled' };
  const project = (projects || []).find((p) => p.id === session.project_id);
  if (!project) return { ok: false, reason: 'no_project_access' };
  const template = (templates || []).find((t) => t.id === templateId);
  if (!template) return { ok: false, reason: 'template_missing' };
  if (!isTemplateUsableForGeneration(template)) return { ok: false, reason: 'template_not_ready' };
  return { ok: true, project, template };
}

/**
 * Whether a row was auto-created by autopilot for this session.
 * @param {object|null} row - projects or customers row (auto_created, auto_source).
 * @param {string} sessionId - Session id.
 * @returns {boolean} True only when the database says so for this exact session.
 */
export function isAutoCreatedFor(row, sessionId) {
  if (!row || row.auto_created !== true) return false;
  const src = row.auto_source && typeof row.auto_source === 'object' ? row.auto_source : {};
  const createdFor = text(pick(src, 'sessionId', 'session_id'));
  return Boolean(createdFor) && createdFor === sessionId;
}

/**
 * Content hash of a requirements row, the same as the worker's requirementRowHash
 * (cloud-run-worker/requirementExtraction.js: FNV-1a of the canonical JSON of title, description,
 * type, priority, acceptance_criteria). The extraction job stores it per row it wrote, so a row
 * whose hash still matches has not been edited since.
 * @param {object} row - requirements row.
 * @returns {string} 8-char hex hash.
 */
export function requirementRowHash(row) {
  return fnv1a32Hex(canonicalJson([
    String(row?.title ?? ''),
    String(row?.description ?? ''),
    String(row?.type ?? ''),
    String(row?.priority ?? ''),
    row?.acceptance_criteria ?? null,
  ]));
}

/**
 * Plan what Undo deletes after the session is moved back to unfiled.
 * @param {object} args - Inputs.
 * @param {string} args.sessionId - Session id.
 * @param {object|null} args.project - Project row (id, name, customer_id, auto_created, auto_source).
 * @param {{sessions: number, documents: number, requirements: number, files: number, members: number}} args.projectUsage - What else uses the project (this session excluded).
 * @param {object|null} args.customer - Customer row (id, name, auto_created, auto_source).
 * @param {{projects: number}} args.customerUsage - Projects under the customer other than this one.
 * @returns {{deleteProject: boolean, deleteCustomer: boolean, projectInUse: boolean, customerInUse: boolean}} Plan.
 */
export function planUndo({ sessionId, project, projectUsage, customer, customerUsage }) {
  const projectAuto = isAutoCreatedFor(project, sessionId);
  const usage = projectUsage || {};
  const projectUsed = ['sessions', 'documents', 'requirements', 'files', 'members'].some((k) => Number(usage[k] || 0) > 0);
  const deleteProject = projectAuto && !projectUsed;
  const customerAuto = isAutoCreatedFor(customer, sessionId);
  const customerUsed = Number(customerUsage?.projects || 0) > 0 || (Boolean(project) && !deleteProject && project.customer_id === customer?.id);
  return {
    deleteProject,
    deleteCustomer: customerAuto && !customerUsed,
    projectInUse: projectAuto && projectUsed,
    customerInUse: customerAuto && customerUsed,
  };
}

/**
 * One "Ready for you" row from a session, its insights and its autopilot record.
 * @param {object} args - Inputs.
 * @param {object} args.session - sessions row (id, name, project_id, created_at, source).
 * @param {object|null} args.insights - session_insights row.
 * @param {object|null} args.record - Normalized autopilot record.
 * @param {Array<object>} args.projects - AppContext projects.
 * @param {Array<object>} args.customers - AppContext customers.
 * @param {Array<object>} args.templates - AppContext templates.
 * @returns {object} Row.
 */
export function buildReadyItem({ session, insights, record, projects, customers, templates }) {
  const filing = record?.filing || null;
  const currentProjectId = session.project_id || null;
  const project = (projects || []).find((p) => p.id === currentProjectId) || null;
  const customer = project ? (customers || []).find((c) => c.id === project.customer_id) || project.customer || null : null;
  const state = filingState(filing, currentProjectId);
  const label = state === 'filed'
    ? filingLabel(filing, { project, customer })
    : project
      ? { path: customer ? `${customer.name} > ${project.name}` : project.name, badge: null }
      : null;
  const followUp = normalizeFollowUp(insights?.follow_up) || record?.followUp || null;
  return {
    sessionId: session.id,
    title: text(session.name) || 'Untitled meeting',
    createdAt: session.created_at || null,
    source: session.source || null,
    running: isAutopilotRunning(record),
    failed: record?.status === 'failed',
    summaryLine: summaryLinesOf(insights, record)[0] || '',
    filing,
    filingState: state,
    filingPath: label?.path || '',
    filingBadge: label?.badge || null,
    canUndo: state === 'filed' && Boolean(filing?.projectId),
    projectId: currentProjectId,
    followUp,
    templates: currentProjectId ? usableTemplateSuggestions(record?.templates || [], templates) : [],
  };
}
